/**
 * @module components/Checkout/useSessionStoredForm
 * @description Custom React hook for persisting checkout form data in sessionStorage.
 * Automatically saves form state and restores on page reload within the same session.
 */

import { useCallback, useEffect } from 'react';

import { isSessionStorageAvailable, safeParse } from 'src/modules/utils';
import { FormState } from 'src/types/forms';

/**
 * @interface UseSessionStoredFormReturn
 * @description Return type for useSessionStoredForm hook
 */
interface UseSessionStoredFormReturn {
  saveStoredData: () => void;
  clearStoredData: () => void;
}

/**
 * @function useSessionStoredForm
 * @description React Hook to persist checkout form data across page reloads.
 * Loads saved data on mount. Does NOT auto-save on changes - use saveStoredData manually.
 * @param form - Current form state object
 * @param init - Initialization function to apply saved form data
 * @returns Object with save and clear functions
 */
export const useSessionStoredForm = (
  form: FormState,
  init: (data: Partial<FormState>) => void,
): UseSessionStoredFormReturn => {
  useEffect(() => {
    if (isSessionStorageAvailable() && typeof init === 'function') {
      const savedData = sessionStorage.getItem('saved-checkout-form-data');
      if (savedData) {
        const savedFormData = safeParse(savedData) as Partial<FormState>;
        if (savedFormData) {
          init(savedFormData);
        }
      }
    }
  }, [init]);

  const saveStoredData = useCallback(() => {
    if (isSessionStorageAvailable()) {
      sessionStorage.setItem('saved-checkout-form-data', JSON.stringify(form));
    }
  }, [form]);

  const clearStoredData = useCallback(() => {
    if (isSessionStorageAvailable()) {
      sessionStorage.removeItem('saved-checkout-form-data');
    }
  }, []);

  return { saveStoredData, clearStoredData };
};
